import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {PageNotices} from '@/components/page-notices'
import {Tag} from '@/components/ui/tag'
import {useDescartarSugerencia, type AnalisisSugerencia} from '@/hooks/sgsst/plan/use-analisis-causa'

/** Las demás acciones se cuentan en una línea para no empujar el plan fuera de la pantalla. */
const MAX_VISIBLES = 3

function AnalisisSugerenciaAviso({
  businessId,
  sugerencias,
  editable,
  onAnalizar,
}: {
  businessId: string
  sugerencias: AnalisisSugerencia[]
  editable: boolean
  onAnalizar: (actionId: string, descripcion: string) => void
}) {
  const {t} = useTranslation()
  const descartar = useDescartarSugerencia(businessId)
  const [todas, setTodas] = useState(false)
  const visibles = todas ? sugerencias : sugerencias.slice(0, MAX_VISIBLES)
  const ocultas = sugerencias.length - visibles.length

  return (
    <div className="surface space-y-3 px-4 py-3.5" data-testid="analisis-sugerencia-aviso">
      <div>
        <p className="text-sm font-medium text-foreground">
          {t('analisis.sugerencia.titulo', {count: sugerencias.length})}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">{t('analisis.sugerencia.cuerpo')}</p>
      </div>
      <ul className="space-y-2">
        {visibles.map((s) => (
          <li
            key={s.action_id}
            data-testid="analisis-sugerencia-row"
            className="flex flex-col gap-2 rounded-lg border border-dashed border-border px-3 py-2.5 sm:flex-row sm:items-center">
            <div className="min-w-0 flex-1">
              <p className="text-sm break-words text-foreground">{s.description}</p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {s.standard_code && (
                  <span className="font-mono text-[11px] text-muted-foreground">{s.standard_code}</span>
                )}
                {s.motivos.map((motivo: string) => (
                  <Tag key={motivo} tone="amber">
                    {t(`analisis.motivo.${motivo}`)}
                  </Tag>
                ))}
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              data-testid="analisis-sugerencia-analizar"
              onClick={() => onAnalizar(s.action_id, s.description)}>
              {t('analisis.sugerencia.analizar')}
            </Button>
            {editable && (
              <Button
                size="sm"
                variant="ghost"
                data-testid="analisis-sugerencia-descartar"
                disabled={descartar.isPending}
                onClick={() => descartar.mutate(s.action_id)}>
                {t('analisis.sugerencia.descartar')}
              </Button>
            )}
          </li>
        ))}
      </ul>
      {ocultas > 0 && (
        <button
          type="button"
          data-testid="analisis-sugerencia-mas"
          onClick={() => setTodas(true)}
          className="cursor-pointer text-sm font-medium text-primary hover:underline">
          {t('analisis.sugerencia.verMas', {count: ocultas})}
        </button>
      )}
    </div>
  )
}

export function PlanSugerenciasNotices({
  businessId,
  sugerencias,
  editable,
  onAnalizar,
}: {
  businessId: string
  sugerencias: AnalisisSugerencia[]
  editable: boolean
  onAnalizar: (id: string, descripcion: string) => void
}) {
  return (
    <PageNotices
      items={[
        sugerencias.length > 0 && {
          id: 'analisis-sugerido',
          severidad: 'amber' as const,
          nodo: (
            <AnalisisSugerenciaAviso
              businessId={businessId}
              sugerencias={sugerencias}
              editable={editable}
              onAnalizar={onAnalizar}
            />
          ),
        },
      ]}
    />
  )
}
